/**
 * Generic WAM registry — single entrypoint for bulk-registering a
 * descriptor list at engine boot.
 *
 * Extracts what Phase A's one-off `registerObxd`-shaped pattern would
 * have duplicated: host-group init, parallel preload, license gating,
 * and dispatch to the appropriate (effect | instrument) adapter. A
 * call site expresses a bundle of WAMs as plain data (`WAM_BUNDLE` in
 * `bundle.ts`) and hands it here; everything else lives in this
 * module.
 *
 * License policy: only descriptors whose license is in
 * `BUNDLABLE_LICENSES` are actually registered. GPL-licensed or
 * unknown-license descriptors silently no-op at this layer — those
 * entries belong in the (future) Bring-Your-Own loader where the
 * user takes ownership of the compliance question.
 *
 * Instrument support is scaffolded but not wired yet: the descriptor
 * shape accepts `kind: 'instrument'`, but the actual adapter + the
 * voice-per-trigger ↔ shared-WAM-instance reconciliation lives in a
 * follow-up PR (see the out-of-scope notes in the plan file). For now
 * an instrument descriptor logs a warn-level message and is skipped.
 */

import type { EffectPlugin, InstrumentPlugin } from '@/audio/plugins/types'
import type { OpenSourceLicense } from '@busswerks/shared'
import { registerEffect, registerInstrument } from '@/audio/plugins/registry'
import { initWamHostGroup, preloadWam } from './bootstrap'
import {
  createWamEffectPlugin,
  type WamEffectDescriptor,
} from './WamEffectAdapter'
import {
  createWamInstrumentPlugin,
  type WamInstrumentDescriptor,
} from './WamInstrumentAdapter'

/**
 * Union descriptor — same shape for effects and instruments, with
 * the `kind` discriminator picking which adapter runs. Keeping them
 * unified lets `bundle.ts` express the whole catalog as one array
 * and lets new adapters plug in without changing the registry's
 * public surface.
 */
export type WamDescriptor = WamEffectRegistration | WamInstrumentRegistration

interface WamCommon {
  /** Registry id — MUST start with `wam-` so the Plugin-tab routes
   *  to `<WamGui>` instead of a React view. */
  id: string
  /** Fetchable URL for the WAM's entry module. Absolute or
   *  Vite-served origin-relative. */
  url: string
  label: string
  /** Spec-compliant OSS license string. The registry only actually
   *  registers descriptors whose license is in `BUNDLABLE_LICENSES`. */
  license: OpenSourceLicense
  description?: string
  /** Optional version string for audit / debugging — not consumed by
   *  the registry itself. */
  version?: string
}

export interface WamEffectRegistration extends WamCommon {
  kind: 'effect'
  category: EffectPlugin['category']
}

export interface WamInstrumentRegistration extends WamCommon {
  kind: 'instrument'
  category: InstrumentPlugin['category']
  /** MIDI note the WAM treats as "pitch 0" — pitch offsets from our
   *  engine are added to this when dispatching note-on events.
   *  Default 60 (middle C). */
  baseMidiNote?: number
  /** Channel (0-15) for note events. Default 0. */
  channel?: number
}

/**
 * Licenses compatible with bundling the WAM's code into our
 * distribution. GPL and custom licenses are deliberately excluded —
 * they route through the (future) BYO loader.
 */
export const BUNDLABLE_LICENSES: ReadonlySet<OpenSourceLicense> = new Set([
  'MIT',
  'BSD-2-Clause',
  'BSD-3-Clause',
  'Apache-2.0',
])

export function isBundlable(license: OpenSourceLicense): boolean {
  return BUNDLABLE_LICENSES.has(license)
}

let registered = false

/**
 * Preload + register the WAMs in `descriptors` against the plugin
 * registry. Idempotent — subsequent calls on the same session no-op
 * so hot-reload doesn't double-register.
 *
 * Steps (all non-fatal on per-descriptor failure):
 *   1. Ensure the host-group id exists for this context.
 *   2. Filter descriptors through the license gate.
 *   3. Preload every URL in parallel (cache hit returns immediately).
 *   4. For each bundlable descriptor whose preload succeeded,
 *      construct + register the appropriate adapter.
 *
 * Returns when all registrations have resolved. Callers that need the
 * boot to block on this (so `Bus.buildSlot` can immediately find the
 * registered plugins) should `await` the promise.
 */
export async function registerWams(
  ctx: AudioContext,
  descriptors: readonly WamDescriptor[],
): Promise<void> {
  if (registered) return
  registered = true

  initWamHostGroup(ctx)

  const bundled = descriptors.filter(d => {
    if (!isBundlable(d.license)) {
      console.info(
        `[wam] skipping ${d.id} — license "${d.license}" is not in the bundled-allowed set`,
      )
      return false
    }
    return true
  })

  // Parallel preload — each URL's network fetch overlaps. `preloadWam`
  // dedupes concurrent requests for the same URL via its inflight
  // cache, so duplicate URLs across descriptors are free.
  await Promise.all(bundled.map(d => preloadWam(ctx, d.url)))

  for (const descriptor of bundled) {
    try {
      registerOne(descriptor)
    } catch (err) {
      console.warn(`[wam] registerWams failed for ${descriptor.id}:`, err)
    }
  }
}

function registerOne(descriptor: WamDescriptor): void {
  if (descriptor.kind === 'effect') {
    const effectDescriptor: WamEffectDescriptor = {
      id: descriptor.id,
      label: descriptor.label,
      category: descriptor.category,
      url: descriptor.url,
    }
    registerEffect(createWamEffectPlugin(effectDescriptor))
    return
  }
  // kind === 'instrument'
  const instrumentDescriptor: WamInstrumentDescriptor = {
    id: descriptor.id,
    label: descriptor.label,
    category: descriptor.category,
    url: descriptor.url,
    baseMidiNote: descriptor.baseMidiNote,
    channel: descriptor.channel,
  }
  registerInstrument(createWamInstrumentPlugin(instrumentDescriptor))
}

/** Test-only: reset the idempotent guard so tests can call repeatedly. */
export function __resetWamRegistryForTests(): void {
  registered = false
}
